<script lang="ts">
	import type { Mediator } from 'types/common'
	import type { ReportJob } from '@isoftdata/svelte-report-job-modal'

	import type { Report } from 'utility/report-viewer-helper'

	import ReportJobModal from '@isoftdata/svelte-report-job-modal'
	import { getContext, onMount, type Component, type ComponentProps } from 'svelte'
	import { graphql, GenerateCrystalReportStore } from '$houdini'
	import session from 'stores/session'
	import loadReports, { saveFavoriteReports } from 'utility/report-viewer-helper'

	interface Props {
		showPreview?: boolean
		storeId?: number
	}

	let { showPreview = true, storeId = $session.currentStore }: Props = $props()

	const mediator = getContext<Mediator>('mediator')

	type ComponentFunctions<Comp extends Component<any, any>> =
		Comp extends Component<any, infer Functions> ? Functions : never

	type OpenFn = Exclude<ComponentFunctions<typeof ReportJobModal>['open'], undefined>
	type Params = Parameters<OpenFn>

	let internalModal: ReportJobModal = $state()!
	let showPrint = $state(false)
	let favoriteReportNames: Array<string> | undefined = $state()
	let report: Report | undefined = $state(undefined) // Set in the open function(if passed) it will be used and the standard loadReports function will not be called

	$effect(() => {
		if (favoriteReportNames) {
			saveFavoriteReports(favoriteReportNames)
		}
	})

	export async function open(reportJob: Params[0], options?: Params[1] & { report?: Report }) {
		const { report: explicitReport, ...reportJobModalOptions } = options ?? {}
		const contextEmails = reportJobModalOptions?.emails?.filter(e => e) ?? []

		report = explicitReport

		await internalModal.open(reportJob, {
			...reportJobModalOptions,
			emails: contextEmails.length ? contextEmails : await loadSuggestedEmails(),
		})
	}

	async function loadSuggestedEmails() {
		const { data } = await getEmailSuggestions.fetch()
		return data?.getEmailSuggestions ?? []
	}

	async function loadPrinters(reportType: string) {
		const { data } = await getPrinters.fetch({
			variables: {
				filter: {
					reportTypes: [reportType],
					storeIds: [storeId],
				},
			},
		})

		showPrint = !!data?.printers?.items?.length

		return data?.printers?.items ?? []
	}

	async function loadDefaultPrinter(reportType: string): Promise<string> {
		const { data } = await getDefaultReportPrinter.fetch({
			variables: {
				input: {
					reportType,
				},
			},
		})
		return data?.getDefaultReportPrinter?.printer ?? ''
	}

	async function generatePdfPreview({
		name,
		type,
		parameters,
		id,
	}: Parameters<ComponentProps<typeof ReportJobModal>['generatePdfPreview']>[0]): Promise<{
		data: string
		mimeType: string
	}> {
		const { data } = await generateCrystalReport.mutate({
			input: {
				name,
				parameters: Object.entries(parameters).map(([key, value]) => ({ key, value })),
				type,
				id,
			},
		})
		if (!data) {
			throw new Error('No data returned from generateCrystalReport')
		}
		return data.generateCrystalReport
	}

	async function saveReportJobs(reportJobs: ReportJob[]): Promise<void> {
		if (!reportJobs.every(job => job.reportId)) {
			return mediator?.publish('showMessage', {
				heading: 'Error saving report jobs',
				message: 'Report ID is required',
				type: 'danger',
				time: false,
			})
		}
		try {
			await Promise.all(
				reportJobs.map(({ parameters, ...reportJob }) => {
					createReportQueueJob.mutate({
						input: {
							...reportJob,
							reportId: reportJob.reportId!,
							parameters: Object.entries(parameters).map(([key, value]) => {
								return {
									parameterName: key,
									value,
								}
							}),
						},
					})
				}),
			)
		} catch (err: any) {
			console.error(err)
			mediator?.publish('showMessage', {
				heading: 'Error saving report jobs',
				message: err.message,
				type: 'danger',
				time: false,
			})
		}
	}

	async function savePrinterPreference(printer: string, reportType: string): Promise<void> {
		try {
			await setDefaultReportPrinterMutation.mutate({
				input: {
					printer,
					reportType,
					storeId,
					userId: $session.id,
				},
			})
		} catch (err) {
			console.error(err)
		}
	}

	const getDefaultReportPrinter = graphql(`
		query GetDefaultReportPrinter($input: GetReportPrinter!) {
			getDefaultReportPrinter(input: $input) {
				printer
			}
		}
	`)

	const setDefaultReportPrinterMutation = graphql(`
		mutation SetDefaultReportPrinterMutation($input: SetDefaultReportPrinter!) {
			setDefaultReportPrinter(input: $input) {
				id
			}
		}
	`)

	const createReportQueueJob = graphql(`
		mutation CreateReportQueueJob($input: NewReportQueue!) {
			createReportQueueJob(input: $input) {
				id
			}
		}
	`)

	const getPrinters = graphql(`
		query GetPrinters($filter: ReportQueuePrinterFilter) {
			printers: reportQueuePrinters(filter: $filter, pagination: { pageSize: 10000, pageNumber: 1 }) {
				items {
					dateLastSeen
					displayName
					hidden
					id
					name
					online
					storeId
				}
			}
		}
	`)

	const generateCrystalReport = new GenerateCrystalReportStore()

	const getEmailSuggestions = graphql(`
		query GetEmailSuggestions {
			getEmailSuggestions
		}
	`)

	const getFavoriteReports = graphql(`
		query GetFavoriteReports {
			session {
				userAccount {
					favoriteReports: settings {
						reportViewer {
							favoriteReportNames
						}
					}
				}
			}
		}
	`)

	onMount(async () => {
		const { data } = await getFavoriteReports.fetch()

		favoriteReportNames = Array.from(
			new Set((data?.session?.userAccount?.favoriteReports?.reportViewer?.favoriteReportNames ?? '').split(';')),
		)
	})
</script>

<!-- @component To use this component, you can simply call its `open` function

```svelte
<script lang="ts">
	import ReportJobModal from 'components/ReportJobModal.svelte'

	let reportJobModal: ReportJobModal | undefined = undefined
	reportJobModal.open(
		{
		// print job details
		type: 'Work Order',
		parameters: { workorderid: workOrder.id.toString() },
		},
		{
		// other options
		},
	)
</script>
<ReportJobModal bind:this={reportJobModal}></ReportJobModal>
```
-->

<ReportJobModal
	{showPrint}
	{showPreview}
	loadReports={async reportType => {
		// If an explicit report was passed, just use that instead of calling loadReports.
		// Having only 1 report in the array will also disable the Report dropdown in the modal. Useful in places like the Report Viewer screen.
		return report
			? [report]
			: await loadReports({
					filter: {
						type: reportType ? [reportType] : undefined,
					},
				})
	}}
	{loadPrinters}
	{loadDefaultPrinter}
	{generatePdfPreview}
	{saveReportJobs}
	{savePrinterPreference}
	bind:favoriteReportNames={() => favoriteReportNames ?? [], v => (favoriteReportNames = v)}
	bind:this={internalModal}
></ReportJobModal>
